import React, { useEffect, useMemo, useState } from "react";
import Shell from "@/components/layout/Shell";
import { PageHeader, PrimaryButton, GhostButton, EmptyState, Field, inputCls, StatusBadge } from "@/components/common";
import { api } from "@/lib/api";
import { Plus, ChevronLeft, ChevronRight, CalendarPlus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const DOW = ["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"];

function daysGrid(year, month) {
  const first = new Date(year, month, 1);
  const startDay = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const isSameDay = (a, b) => a && b && a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();

export default function Agenda() {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [apps, setApps] = useState([]);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [form, setForm] = useState({ client_id:"", team_member_id:"", title:"", meeting_type:"seguimiento", scheduled_at:"", duration_min:60, notes:"" });

  const load = async () => {
    const [a,c,t] = await Promise.all([api.get("/appointments"), api.get("/clients"), api.get("/team")]);
    setApps(a.data); setClients(c.data); setTeam(t.data);
  };
  useEffect(() => { load(); }, []);

  const monthApps = useMemo(() => {
    const y = cursor.getFullYear(), m = cursor.getMonth();
    return apps.filter(a => {
      const d = new Date(a.scheduled_at);
      return d.getFullYear()===y && d.getMonth()===m;
    });
  }, [apps, cursor]);

  const cells = daysGrid(cursor.getFullYear(), cursor.getMonth());

  const openNew = (date) => {
    const dt = date || today;
    const iso = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 10, 0).toISOString().slice(0,16);
    setForm({ client_id: clients[0]?.client_id || "", team_member_id: team[0]?.member_id || "", title:"", meeting_type:"seguimiento", scheduled_at: iso, duration_min: 60, notes:"" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error("Selecciona un cliente"); return; }
    if (!form.title.trim()) { toast.error("El título es obligatorio"); return; }
    if (!form.scheduled_at) { toast.error("Selecciona fecha y hora"); return; }
    try {
      const payload = { ...form, scheduled_at: new Date(form.scheduled_at).toISOString(), duration_min: Number(form.duration_min) || 60 };
      if (!payload.team_member_id) delete payload.team_member_id;
      await api.post("/appointments", payload);
      toast.success("Cita creada"); setOpen(false); load();
    } catch { toast.error("No se pudo crear la cita"); }
  };

  const remove = async (a) => {
    if (!window.confirm("¿Eliminar esta cita?")) return;
    await api.delete(`/appointments/${a.appointment_id}`);
    toast.success("Cita eliminada"); load();
  };

  const clientMap = Object.fromEntries(clients.map(c => [c.client_id, c.name]));
  const teamMap = Object.fromEntries(team.map(t => [t.member_id, t.name]));

  const selectedDayApps = selectedDay ? apps.filter(a => isSameDay(new Date(a.scheduled_at), selectedDay)) : [];

  return (
    <Shell>
      <PageHeader
        title="Agenda"
        subtitle={`${monthApps.length} citas en ${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`}
        action={<PrimaryButton data-testid="add-appointment-btn" onClick={()=>openNew(today)}><Plus className="w-4 h-4"/> Nueva cita</PrimaryButton>}
      />

      <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-5">
          <button data-testid="prev-month-btn" onClick={()=>setCursor(new Date(cursor.getFullYear(), cursor.getMonth()-1, 1))} className="p-2 rounded-lg hover:bg-[#1A1D24] text-[#9CA3AF] hover:text-[#F5F5F7] transition-colors">
            <ChevronLeft className="w-5 h-5" strokeWidth={1.75} />
          </button>
          <h2 className="text-lg font-bold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>
            {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
          </h2>
          <button data-testid="next-month-btn" onClick={()=>setCursor(new Date(cursor.getFullYear(), cursor.getMonth()+1, 1))} className="p-2 rounded-lg hover:bg-[#1A1D24] text-[#9CA3AF] hover:text-[#F5F5F7] transition-colors">
            <ChevronRight className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-2 mb-2">
          {DOW.map(d => <div key={d} className="text-xs text-[#9CA3AF] text-center font-medium py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {cells.map((d, i) => {
            if (!d) return <div key={i} className="min-h-[90px]" />;
            const isToday = isSameDay(d, today);
            const dayApps = apps.filter(a => isSameDay(new Date(a.scheduled_at), d));
            return (
              <button
                key={i}
                data-testid={`day-cell-${d.getDate()}`}
                onClick={()=>setSelectedDay(d)}
                className={`min-h-[90px] rounded-xl p-2 text-left transition-colors duration-200 ${
                  isToday ? "border-2 border-[#F97316] bg-[#F97316]/5" : "border border-[#262A33] bg-[#0B0D12] hover:bg-[#1A1D24]"
                }`}
              >
                <div className={`text-xs font-semibold ${isToday ? "text-[#F97316]" : "text-[#F5F5F7]"}`}>{d.getDate()}</div>
                <div className="mt-1 space-y-1">
                  {dayApps.slice(0,2).map(a => (
                    <div key={a.appointment_id} className="text-[10px] bg-[#F97316]/10 text-[#F97316] rounded px-1.5 py-0.5 truncate">
                      {new Date(a.scheduled_at).toLocaleTimeString("es-CO",{hour:"2-digit",minute:"2-digit"})} {a.title}
                    </div>
                  ))}
                  {dayApps.length > 2 && <div className="text-[10px] text-[#9CA3AF]">+{dayApps.length-2} más</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {clients.length === 0 && (
        <div className="mt-6">
          <EmptyState icon={CalendarPlus} title="Añade clientes primero" description="Para agendar citas necesitas al menos un cliente en el sistema." />
        </div>
      )}

      <Dialog open={!!selectedDay} onOpenChange={(o)=>!o && setSelectedDay(null)}>
        <DialogContent className="bg-[#15171E] border-[#262A33] text-[#F5F5F7]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>
              Citas del {selectedDay?.toLocaleDateString("es-CO",{day:"numeric",month:"long",year:"numeric"})}
            </DialogTitle>
          </DialogHeader>
          {selectedDayApps.length === 0 ? (
            <p className="text-sm text-[#9CA3AF] py-4">No hay citas para este día.</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-auto">
              {selectedDayApps.map(a => (
                <div key={a.appointment_id} className="p-3 rounded-xl bg-[#0B0D12] border border-[#262A33] flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-[#F5F5F7] font-medium">{a.title}</div>
                    <div className="text-xs text-[#9CA3AF] mt-0.5">
                      {new Date(a.scheduled_at).toLocaleTimeString("es-CO",{hour:"2-digit",minute:"2-digit"})} · {clientMap[a.client_id] || "Cliente"}
                      {a.team_member_id && ` · ${teamMap[a.team_member_id] || ""}`}
                    </div>
                    <div className="mt-1"><StatusBadge status={a.status} /></div>
                  </div>
                  <button onClick={()=>remove(a)} className="p-1.5 rounded-lg text-[#6B7280] hover:text-red-400 hover:bg-red-500/10 transition-colors" aria-label="Eliminar">
                    <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <PrimaryButton onClick={()=>{ openNew(selectedDay); setSelectedDay(null); }}>
              <Plus className="w-4 h-4"/> Añadir cita este día
            </PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#15171E] border-[#262A33] text-[#F5F5F7] max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>Nueva cita</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Cliente *">
              <select data-testid="appointment-client-select" required className={inputCls} value={form.client_id} onChange={e=>setForm({...form, client_id: e.target.value})}>
                <option value="">Selecciona un cliente</option>
                {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Título *">
              <input data-testid="appointment-title-input" required className={inputCls} value={form.title} onChange={e=>setForm({...form, title: e.target.value})} placeholder="Presentación de resultados"/>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tipo">
                <select className={inputCls} value={form.meeting_type} onChange={e=>setForm({...form, meeting_type: e.target.value})}>
                  <option value="seguimiento">Llamada de seguimiento</option>
                  <option value="contenido">Sesión de contenido</option>
                  <option value="resultados">Presentación de resultados</option>
                  <option value="consultoria">Reunión de consultoría</option>
                  <option value="otro">Otro</option>
                </select>
              </Field>
              <Field label="Responsable">
                <select className={inputCls} value={form.team_member_id} onChange={e=>setForm({...form, team_member_id: e.target.value})}>
                  <option value="">(sin asignar)</option>
                  {team.map(t => <option key={t.member_id} value={t.member_id}>{t.name}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Fecha y hora *">
                <input data-testid="appointment-datetime-input" required type="datetime-local" className={inputCls} value={form.scheduled_at} onChange={e=>setForm({...form, scheduled_at: e.target.value})}/>
              </Field>
              <Field label="Duración (min)">
                <input type="number" min="15" step="15" className={inputCls} value={form.duration_min} onChange={e=>setForm({...form, duration_min: e.target.value})}/>
              </Field>
            </div>
            <Field label="Notas">
              <textarea rows={2} className={inputCls} value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})}/>
            </Field>
            <DialogFooter>
              <GhostButton type="button" onClick={()=>setOpen(false)}>Cancelar</GhostButton>
              <PrimaryButton data-testid="save-appointment-btn" type="submit">Crear cita</PrimaryButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
